import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, CheckCircle, RefreshCw, RotateCcw, X, Download } from 'lucide-react';
import MainService from '../../services/MainService';
import DataColumnMenu from '../ui/DataColumnMenu';
import { columnHasActiveFilter, groupRows } from '../ui/columnMenuUtils';
import { useGroupCollapse, GroupByBanner, GroupHeaderLabel } from '../ui/groupCollapse';
import { flattenFiltersForApi } from '../../utils/listFilters';
import { PAGE_SIZE_OPTIONS, buildPageItems } from '../../utils/listPagination';
import { exportGroupedExcel } from '../../utils/excelExport';

const REVERSE_COLUMNS = [
  { field: 'voucherno', title: 'Voucher No', widthClass: 'w-[5%]', hideable: true },
  { field: 'clientbillno', title: 'Client Bill', widthClass: 'w-[5%]', hideable: true },
  { field: 'deliverydate', title: 'Delivery Date', widthClass: 'w-[5%]', hideable: true },
  { field: 'productprice', title: 'Product Price', widthClass: 'w-[5%]', hideable: true },
  { field: 'deliverycharge', title: 'Delivery Charge', widthClass: 'w-[5%]', hideable: true },
  { field: 'tax', title: 'Tax', widthClass: 'w-[4%]', hideable: true },
  { field: 'customeramount', title: 'Customer Amount', widthClass: 'w-[5%]', hideable: true },
  { field: 'clientamount', title: 'Client Amount', widthClass: 'w-[5%]', hideable: true },
  { field: 'clientname', title: 'Client', widthClass: 'w-[6%]', hideable: true },
  { field: 'clientmob', title: 'Client Mob', widthClass: 'w-[5%]', hideable: true },
  { field: 'clientemirate', title: 'Client Emirate', widthClass: 'w-[5%]', hideable: true },
  { field: 'clientlocation', title: 'Client Location', widthClass: 'w-[5%]', hideable: true },
  { field: 'customername', title: 'Customer', widthClass: 'w-[6%]', hideable: true },
  { field: 'customermob', title: 'Customer Mob', widthClass: 'w-[5%]', hideable: true },
  { field: 'customeremirate', title: 'Customer Emirate', widthClass: 'w-[5%]', hideable: true },
  { field: 'customerlocation', title: 'Customer Location', widthClass: 'w-[5%]', hideable: true },
  { field: 'status', title: 'Status', widthClass: 'w-[5%]', hideable: true },
  { field: 'actions', title: 'Options', widthClass: 'w-[5%]', hideable: true, sortable: false, filterable: false, groupable: false, align: 'center' },
];

const defaultVisibility = () =>
  REVERSE_COLUMNS.reduce((acc, col) => {
    acc[col.field] = true;
    return acc;
  }, {});

const parseList = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.Data)) return response.Data;
  if (Array.isArray(response?.data)) return response.data;
  return [];
};

const parseTotal = (response, fallback = 0) => Number(response?.Total ?? response?.total ?? fallback) || 0;

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('en-GB');
};

const formatAmount = (value) => Number(value || 0).toFixed(2);
const locationName = (address) => address?.Location?.Name || '-';
const emirateName = (address) => address?.Emirate?.Name || '-';

const getCellValue = (row, field) => {
  switch (field) {
    case 'voucherno':
      return row.VoucherNo || '-';
    case 'clientbillno':
      return row.ClientBillNo || '-';
    case 'deliverydate':
      return formatDate(row.DeliveryDate);
    case 'productprice':
      return formatAmount(row.ProductPrice);
    case 'deliverycharge':
      return formatAmount(row.DeliveryCharge);
    case 'tax':
      return formatAmount(row.TAX);
    case 'customeramount':
      return formatAmount(row.CustomerAmount);
    case 'clientamount':
      return formatAmount(row.ClientAmount);
    case 'clientname':
      return row.Client?.Name || '-';
    case 'clientmob':
      return row.Client?.MobileNo1 || '-';
    case 'clientemirate':
      return emirateName(row.ClientAddress);
    case 'clientlocation':
      return locationName(row.ClientAddress);
    case 'customername':
      return row.Customer?.Name || '-';
    case 'customermob':
      return row.Customer?.MobileNo1 || '-';
    case 'customeremirate':
      return emirateName(row.CustomerAddress);
    case 'customerlocation':
      return locationName(row.CustomerAddress);
    case 'status':
      return row.Status?.Name || '-';
    default:
      return row[field];
  }
};

const ReverseCancel = () => {
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [reverseTarget, setReverseTarget] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');
  const [filtersByField, setFiltersByField] = useState({});
  const [columnVisibility, setColumnVisibility] = useState(defaultVisibility);
  const [groupField, setGroupField] = useState(null);
  const loadSeqRef = useRef(0);
  const pageSizeBeforeGroupRef = useRef(null);

  useEffect(() => {
    if (!error) return undefined;
    const timer = setTimeout(() => setError(''), 5000);
    return () => clearTimeout(timer);
  }, [error]);

  useEffect(() => {
    if (!success) return undefined;
    const timer = setTimeout(() => setSuccess(''), 3000);
    return () => clearTimeout(timer);
  }, [success]);

  const fetchRows = useCallback(async ({
    page: pageArg,
    pageSize: pageSizeArg,
    sortField: sortFieldArg,
    sortDirection: sortDirectionArg,
    filters: filtersArg,
  } = {}) => {
    const pageNum = pageArg ?? page;
    const size = pageSizeArg !== undefined ? pageSizeArg : pageSize;
    const sField = sortFieldArg !== undefined ? sortFieldArg : sortField;
    const sDir = sortDirectionArg !== undefined ? sortDirectionArg : sortDirection;
    const filters = filtersArg !== undefined ? filtersArg : filtersByField;
    const seq = ++loadSeqRef.current;
    const isAll = size === 'all';

    try {
      setLoading(true);
      setError('');
      const body = {
        Skip: isAll ? 0 : (pageNum - 1) * Number(size),
        Take: isAll ? 0 : Number(size),
        Keyword: null,
        SortField: sField || null,
        SortDir: sDir || 'asc',
        Filters: flattenFiltersForApi(filters),
      };

      const response = await MainService.CancelledZeroOrdersList(body);
      if (seq !== loadSeqRef.current) return;
      const list = parseList(response);
      setRows(list);
      setTotalCount(parseTotal(response, list.length));
    } catch (err) {
      if (seq !== loadSeqRef.current) return;
      console.error('Load reverse cancel orders error:', err);
      setRows([]);
      setTotalCount(0);
      setError('Failed to load cancelled orders');
    } finally {
      if (seq === loadSeqRef.current) setLoading(false);
    }
  }, [page, pageSize, sortField, sortDirection, filtersByField]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const effectivePageSize = pageSize === 'all' ? Math.max(totalCount, 1) : Number(pageSize);
  const totalPages = pageSize === 'all' ? 1 : Math.max(1, Math.ceil(totalCount / effectivePageSize) || 1);
  const pageItems = useMemo(() => buildPageItems(page, totalPages), [page, totalPages]);
  const rangeFrom = totalCount === 0 ? 0 : (pageSize === 'all' ? 1 : (page - 1) * Number(pageSize) + 1);
  const rangeTo = totalCount === 0 ? 0 : (pageSize === 'all' ? totalCount : Math.min(page * Number(pageSize), totalCount));
  const colSpan = useMemo(
    () => REVERSE_COLUMNS.filter((c) => columnVisibility[c.field] !== false).length + 1,
    [columnVisibility]
  );
  const activeFilterCount = useMemo(
    () => Object.keys(filtersByField).filter((f) => columnHasActiveFilter(filtersByField, f)).length,
    [filtersByField]
  );

  const displayGroups = useMemo(() => {
    if (!groupField) return [{ key: null, label: null, rows }];
    const mapped = rows.map((r) => ({ ...r, [groupField]: getCellValue(r, groupField) }));
    return groupRows(mapped, groupField);
  }, [rows, groupField]);

  const { isCollapsed, toggleGroup, collapseAll, expandAll } = useGroupCollapse(groupField);

  const groupKeys = useMemo(
    () => displayGroups.filter((g) => g.label != null).map((g) => g.key),
    [displayGroups]
  );

  const refresh = () => {
    setPage(1);
    setSortField('');
    setSortDirection('asc');
    setFiltersByField({});
    setGroupField(null);
    pageSizeBeforeGroupRef.current = null;
    fetchRows({
      page: 1,
      sortField: '',
      sortDirection: 'asc',
      filters: {},
    });
  };

  const handleGroupBy = (field) => {
    if (!groupField) {
      pageSizeBeforeGroupRef.current = { pageSize, page };
    }
    setGroupField(field);
    if (pageSize !== 'all') {
      setPage(1);
      setPageSize('all');
    }
  };

  const handleUngroup = () => {
    const prev = pageSizeBeforeGroupRef.current;
    setGroupField(null);
    pageSizeBeforeGroupRef.current = null;
    if (prev) {
      setPageSize(prev.pageSize);
      setPage(prev.page);
    }
  };

  const handlePageSizeChange = (value) => {
    const next = value === 'all' ? 'all' : Number(value);
    setPageSize(next);
    setPage(1);
    fetchRows({ page: 1, pageSize: next });
  };

  const goToPage = (nextPage) => {
    if (nextPage < 1 || nextPage > totalPages || nextPage === page) return;
    setPage(nextPage);
    fetchRows({ page: nextPage });
  };

  const handleColumnSort = (field, direction) => {
    setSortField(field);
    setSortDirection(direction);
    setPage(1);
    fetchRows({ page: 1, sortField: field, sortDirection: direction });
  };

  const handleToggleColumn = (field) => {
    setColumnVisibility((prev) => {
      const nextVisible = { ...prev, [field]: prev[field] === false };
      const stillVisible = REVERSE_COLUMNS.some((c) => nextVisible[c.field] !== false);
      if (!stillVisible) return prev;
      return nextVisible;
    });
  };

  const handleApplyColumnFilter = (field, filter) => {
    const nextFilters = { ...filtersByField, [field]: filter };
    setFiltersByField(nextFilters);
    setPage(1);
    fetchRows({ page: 1, filters: nextFilters });
  };

  const handleClearColumnFilter = (field) => {
    const nextFilters = { ...filtersByField };
    delete nextFilters[field];
    setFiltersByField(nextFilters);
    setPage(1);
    fetchRows({ page: 1, filters: nextFilters });
  };

  const clearSortAndFilters = () => {
    const prev = pageSizeBeforeGroupRef.current;
    setFiltersByField({});
    setSortField('');
    setSortDirection('asc');
    setGroupField(null);
    pageSizeBeforeGroupRef.current = null;
    setPage(1);
    if (prev && prev.pageSize !== pageSize) {
      setPageSize(prev.pageSize);
    }
    fetchRows({
      page: 1,
      pageSize: prev?.pageSize ?? pageSize,
      sortField: '',
      sortDirection: 'asc',
      filters: {},
    });
  };

  const exportToExcel = () => {
    try {
      exportGroupedExcel({
        sheetName: 'Reverse Cancel',
        fileName: `reverse_cancel_${new Date().toISOString().split('T')[0]}.xlsx`,
        headers: ['#', ...REVERSE_COLUMNS.filter((c) => c.field !== 'actions').map((c) => c.title)],
        groups: displayGroups,
        groupFieldTitle: REVERSE_COLUMNS.find((c) => c.field === groupField)?.title || 'Group',
        mapRow: (row, serial) => [
          serial,
          ...REVERSE_COLUMNS.filter((c) => c.field !== 'actions').map((c) => getCellValue(row, c.field)),
        ],
      });
      setSuccess('Excel file exported successfully');
    } catch (err) {
      console.error('Export error:', err);
      if (err?.message === 'No rows to export') {
        setError('No cancelled orders to export');
      } else {
        setError('Failed to export Excel file');
      }
    }
  };

  const confirmReverse = async () => {
    if (!reverseTarget?.Id) return;

    try {
      setSaving(true);
      setError('');
      const response = await MainService.ReverseCancelledOrders(reverseTarget.Id);
      if (response === 'Success') {
        setSuccess('Reversed');
        setReverseTarget(null);
        await fetchRows();
      } else {
        setError(String(response || 'Failed to reverse order'));
      }
    } catch (err) {
      console.error('Reverse cancelled order error:', err);
      setError('Network Error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {error && <Toast type="error" message={error} onClose={() => setError('')} />}
      {success && <Toast type="success" message={success} onClose={() => setSuccess('')} />}

      <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Reverse Cancelled Orders</h1>
            <p className="mt-2 text-sm text-amber-700">
              Note: only cancelled orders with client amount 0 will be shown to reverse.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={exportToExcel}
              disabled={rows.length === 0}
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              <Download className="h-4 w-4" />
              Export
            </button>
            <button
              type="button"
              onClick={refresh}
              disabled={loading || saving}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-gray-100 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-200 disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>
      </div>

      <div className="flex flex-col rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="flex flex-col gap-2 border-b border-gray-200 p-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Reverse Cancel List</h2>
            <p className="text-sm text-gray-500">
              {totalCount === 0 ? '0 cancelled orders' : `${rangeFrom}–${rangeTo} of ${totalCount}`}
            </p>
          </div>
        </div>

        <div className="master-table-scroll relative overflow-x-auto" style={{ maxHeight: 560 }}>
          {loading && (
            <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/75 backdrop-blur-sm">
              <div className="flex flex-col items-center gap-2">
                <RefreshCw className="h-8 w-8 animate-spin text-primary" />
                <span className="text-sm text-gray-600">Loading...</span>
              </div>
            </div>
          )}

          {(activeFilterCount > 0 || sortField) && (
            <div className="flex items-center justify-between gap-2 border-b border-gray-100 bg-[#f0f9f4] px-4 py-2 text-sm text-gray-700">
              <span>
                {sortField
                  ? `Sorted by ${REVERSE_COLUMNS.find((c) => c.field === sortField)?.title || sortField} (${sortDirection})`
                  : 'Filters active'}
                {activeFilterCount > 0 ? ` · ${activeFilterCount} column filter(s)` : ''}
              </span>
              <button type="button" onClick={clearSortAndFilters} className="text-primary hover:underline">
                Clear sort & filters
              </button>
            </div>
          )}

          {groupField && (
            <GroupByBanner
              groupTitle={REVERSE_COLUMNS.find((c) => c.field === groupField)?.title || groupField}
              groupKeys={groupKeys}
              onUngroup={handleUngroup}
              onCollapseAll={collapseAll}
              onExpandAll={expandAll}
            />
          )}

          <table className="w-full min-w-[1800px] table-fixed">
            <thead className="sticky top-0 z-10 bg-gray-50">
              <tr>
                <th className="w-[3%] px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">#</th>
                {REVERSE_COLUMNS.map((column) =>
                  columnVisibility[column.field] === false ? null : (
                    <DataColumnMenu
                      key={column.field}
                      column={column}
                      columns={REVERSE_COLUMNS}
                      sortField={sortField}
                      sortDirection={sortDirection}
                      onSort={handleColumnSort}
                      columnVisibility={columnVisibility}
                      onToggleColumn={handleToggleColumn}
                      filtersByField={filtersByField}
                      onApplyFilter={handleApplyColumnFilter}
                      onClearFilter={handleClearColumnFilter}
                      groupField={groupField}
                      onGroupBy={handleGroupBy}
                      onUngroup={handleUngroup}
                      align={column.align}
                    />
                  )
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={colSpan} className="px-4 py-10 text-center text-sm text-gray-500">
                    No cancelled orders found.
                  </td>
                </tr>
              ) : (
                (() => {
                  let serial = 0;
                  return displayGroups.flatMap((group) => {
                    const block = [];
                    if (group.label != null) {
                      block.push(
                        <tr key={`group-${group.key}`} className="bg-gray-100">
                          <td colSpan={colSpan} className="px-4 py-2">
                            <GroupHeaderLabel
                              title={REVERSE_COLUMNS.find((c) => c.field === groupField)?.title}
                              label={group.label}
                              count={group.rows.length}
                              collapsed={isCollapsed(group.key)}
                              onToggle={() => toggleGroup(group.key)}
                            />
                          </td>
                        </tr>
                      );
                    }
                    if (group.label != null && isCollapsed(group.key)) {
                      return block;
                    }
                    group.rows.forEach((row, index) => {
                      serial += 1;
                      block.push(
                        <tr key={row.Id || `${group.key}-${index}`} className="hover:bg-gray-50">
                          <td className="whitespace-nowrap px-4 py-2 text-sm text-gray-900">
                            {groupField || pageSize === 'all' ? serial : (page - 1) * Number(pageSize) + serial}
                          </td>
                          {REVERSE_COLUMNS.map((column) =>
                            columnVisibility[column.field] === false ? null : (
                              <td
                                key={column.field}
                                className={`whitespace-nowrap px-4 py-2 text-sm ${
                                  column.field === 'voucherno' ? 'font-semibold text-gray-900' : 'text-gray-600'
                                } ${column.field === 'actions' ? 'text-center' : ''}`}
                              >
                                {column.field === 'actions' ? (
                                  <button
                                    type="button"
                                    onClick={() => setReverseTarget(row)}
                                    disabled={saving}
                                    className="inline-flex items-center gap-2 rounded-lg bg-green-50 px-3 py-1.5 text-xs font-semibold text-green-700 hover:bg-green-100 disabled:opacity-50"
                                  >
                                    <RotateCcw className="h-3.5 w-3.5" />
                                    Reverse Order
                                  </button>
                                ) : (
                                  getCellValue(row, column.field)
                                )}
                              </td>
                            )
                          )}
                        </tr>
                      );
                    });
                    return block;
                  });
                })()
              )}
            </tbody>
          </table>
        </div>

        <div className="border-t border-gray-200 bg-gray-50 px-4 py-3">
          <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-gray-600">
            <div className="flex flex-wrap items-center gap-3">
              <span>
                {totalCount === 0 ? '0 cancelled orders' : `${rangeFrom}–${rangeTo} of ${totalCount}`}
                {activeFilterCount > 0 ? ` · ${activeFilterCount} filter(s)` : ''}
                {groupField
                  ? ` · grouped by ${REVERSE_COLUMNS.find((c) => c.field === groupField)?.title || groupField}`
                  : ''}
              </span>
              <label className="flex items-center gap-2">
                <span className="text-gray-500">Rows</span>
                <select
                  value={pageSize}
                  onChange={(e) => handlePageSizeChange(e.target.value)}
                  disabled={loading || !!groupField}
                  className="rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm focus:border-[#46955A] focus:outline-none"
                >
                  {PAGE_SIZE_OPTIONS.map((opt) => (
                    <option key={String(opt)} value={opt}>
                      {opt === 'all' ? 'All' : opt}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            {!groupField && pageSize !== 'all' && (
              <div className="flex flex-wrap items-center gap-1">
                <button
                  type="button"
                  disabled={page <= 1 || loading}
                  onClick={() => goToPage(page - 1)}
                  className="inline-flex items-center rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Prev
                </button>
                {pageItems.map((item, idx) =>
                  item === '…' ? (
                    <span key={`e-${idx}`} className="px-1.5 text-gray-400">…</span>
                  ) : (
                    <button
                      key={item}
                      type="button"
                      disabled={loading}
                      onClick={() => goToPage(item)}
                      className={`min-w-[2rem] rounded-lg px-2 py-1.5 text-sm transition-colors ${
                        item === page
                          ? 'bg-[#46955A] font-medium text-white'
                          : 'border border-gray-200 bg-white text-gray-700 hover:bg-[#f0f9f4]'
                      }`}
                    >
                      {item}
                    </button>
                  )
                )}
                <button
                  type="button"
                  disabled={page >= totalPages || loading}
                  onClick={() => goToPage(page + 1)}
                  className="inline-flex items-center rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {reverseTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-xl bg-white shadow-xl">
            <div className="border-b border-gray-200 p-5">
              <div className="flex items-center gap-3">
                <div className="rounded-full bg-green-100 p-2 text-green-600">
                  <RotateCcw className="h-5 w-5" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Confirm Reverse Order</h3>
              </div>
            </div>
            <div className="p-5 text-sm text-gray-600">
              Click confirm to reverse cancelled voucher <span className="font-semibold text-gray-900">{reverseTarget.VoucherNo}</span>.
            </div>
            <div className="flex justify-end gap-3 border-t border-gray-200 p-5">
              <button
                type="button"
                onClick={() => setReverseTarget(null)}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmReverse}
                disabled={saving}
                className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90 disabled:opacity-50"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Toast = ({ type, message, onClose }) => {
  const isError = type === 'error';
  return (
    <div className={`fixed right-4 top-4 z-50 flex min-w-[280px] items-center gap-3 rounded-lg px-4 py-3 text-white shadow-lg ${isError ? 'bg-red-500' : 'bg-green-500'}`}>
      {isError ? <AlertTriangle className="h-5 w-5" /> : <CheckCircle className="h-5 w-5" />}
      <span className="flex-1 text-sm font-medium">{message}</span>
      <button type="button" onClick={onClose} className="rounded p-1 hover:bg-white/20">
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};

export default ReverseCancel;
